/**
 * UI-8c Task 6 — ScheduleRotationDialog.
 *
 * Modal dialog for scheduling a future DEK rotation. Picks a single
 * `scheduledFor` datetime (browser local input → submitted as a Date object
 * to the parent). The parent (KeyRotationPage) handles the POST + toast.
 *
 * Defaults:
 *   - The Calendar opens at *tomorrow 02:00 UTC* — typical "low-traffic
 *     window" for an enterprise CCaaS deployment. The operator can
 *     override before submitting.
 *
 * Validation:
 *   - Backend enforces "must be future" but rejects bursts that race the
 *     scheduler loop. We apply a client-side 5-minute floor so the operator
 *     can't accidentally schedule a rotation for "now + 30 seconds" and have
 *     it bounce.
 *   - When `when < now + 5 min` the Schedule button is disabled and an
 *     inline error message explains why.
 *
 * Why a Dialog instead of a Drawer?
 * --------------------------------
 * Scheduling is a one-shot operation (commit-and-close). Centred modal
 * matches the same UX as BulkTagDialog / DncImportDialog elsewhere in the
 * admin app — keeps muscle memory consistent.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Calendar } from 'primereact/calendar'
import { X } from 'lucide-react'

export interface ScheduleRotationDialogProps {
  open: boolean
  tenantSlug: string
  onClose: () => void
  onSubmit: (when: Date) => void
}

const FIVE_MINUTES_MS = 5 * 60 * 1000

function defaultWhen(): Date {
  const t = new Date()
  t.setUTCDate(t.getUTCDate() + 1)
  t.setUTCHours(2, 0, 0, 0)
  return t
}

export function ScheduleRotationDialog({
  open,
  tenantSlug,
  onClose,
  onSubmit,
}: ScheduleRotationDialogProps): JSX.Element {
  const initial = useMemo(() => defaultWhen(), [open])
  const [when, setWhen] = useState<Date>(initial)

  useEffect(() => {
    if (open) setWhen(initial)
  }, [open, initial])

  const tooSoon = when.getTime() < Date.now() + FIVE_MINUTES_MS

  if (!open) return <></>

  return (
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center">
              <i className="pi pi-calendar text-white text-sm" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Schedule Rotation</h3>
              <p className="text-xs text-gray-400">{tenantSlug}</p>
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="schedule-rotation-when" className="text-sm font-semibold text-gray-700">Scheduled for <span className="text-red-500">*</span></label>
            <Calendar
              inputId="schedule-rotation-when"
              value={when}
              onChange={(e) => { const v = e.value; if (v instanceof Date) setWhen(v) }}
              showTime
              showIcon
              pt={{
                root: { className: 'w-full' },
                input: { className: 'w-full h-11 pl-4 pr-10 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 outline-none transition-all' },
              } as any}
            />
            {tooSoon && <p className="text-xs text-red-500">Must be at least 5 minutes from now.</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <button onClick={onClose} className="text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-white transition-all">
            Cancel
          </button>
          <button
            onClick={() => { if (!tooSoon) onSubmit(when) }}
            disabled={tooSoon}
            className="text-sm font-semibold px-5 py-2.5 rounded-sm bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 text-white transition-all"
          >
            Schedule
          </button>
        </div>
      </div>
    </>
  )
}

export default ScheduleRotationDialog
